import React from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { TextField, Typography, FormHelperText, makeStyles } from '@material-ui/core'
import { Add } from '@material-ui/icons'
import { colors } from 'utils/colors'

const CreateNewRow = ({ classes, createNewLabel, onCreateNew }) => (
  <div
    className={classes.createNewHeader}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onCreateNew}>
    <Add />
    <Typography className={classes.buttonItem}>{createNewLabel}</Typography>
  </div>
)

// Renders the "Create new" row above the scrollable option list (as a sibling of the <ul>,
// not inside it) so it stays pinned in view instead of scrolling away with the options.
const CreateNewListbox = React.forwardRef(function CreateNewListbox(props, ref) {
  const { children, createNewOption, createNewLabel, onCreateNew, classes, ...listboxProps } = props
  return (
    <>
      {createNewOption && (
        <CreateNewRow classes={classes} createNewLabel={createNewLabel} onCreateNew={onCreateNew} />
      )}
      <ul ref={ref} {...listboxProps}>
        {children}
      </ul>
    </>
  )
})

const MultiAutocomplete = ({
  options = [],
  value = [],
  onChange,
  label = 'Items',
  placeholder = label,
  displayField,
  valueField = 'id',
  error = '',
  warningText = '',
  createNewOption = true,
  createNewLabel = `Create ${label.toLowerCase()}`,
  onCreateNew,
  ...props
}) => {
  const classes = useStyles()

  // Map selected ids to their corresponding objects
  const selectedOptions = React.useMemo(
    () =>
      value.map((itemId) => options.find((item) => item[valueField] === itemId)).filter(Boolean),
    [value, options, valueField]
  )

  const handleChange = (_event, newValue) => {
    const selectedIds = newValue.map((item) => item[valueField])
    onChange(selectedIds)
  }

  return (
    <>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        getOptionLabel={(option) => option[displayField] || ''}
        ListboxComponent={CreateNewListbox}
        ListboxProps={{ createNewOption, createNewLabel, onCreateNew, classes }}
        classes={{ noOptions: classes.noOptions }}
        noOptionsText={
          createNewOption ? (
            <CreateNewRow
              classes={classes}
              createNewLabel={createNewLabel}
              onCreateNew={onCreateNew}
            />
          ) : (
            'No options'
          )
        }
        renderOption={(option) => <li className={classes.listItem}>{option[displayField]}</li>}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!error}
            variant="outlined"
            label={label}
            placeholder={placeholder}
            {...props}
          />
        )}
      />
      {error && <FormHelperText className={classes.error}>{error}</FormHelperText>}
      {!error && warningText && (
        <FormHelperText className={classes.warning}>{warningText}</FormHelperText>
      )}
    </>
  )
}

export default MultiAutocomplete

const useStyles = makeStyles({
  noOptions: {
    padding: 0
  },
  createNewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '6px 16px',
    color: colors.indigo,
    cursor: 'pointer',
    borderBottom: '1px solid rgba(0,0,0,0.12)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)'
    }
  },
  buttonItem: {
    color: colors.indigo,
    fontWeight: 600
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  chip: {
    background: '#c62828',
    color: '#FFFFFF'
  },
  chipLabel: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    fontWeight: 'bold'
  },
  error: {
    color: '#f44336',
    marginLeft: '14px'
  },
  warning: {
    color: '#b58105',
    marginLeft: '14px'
  }
})
